// src/store/slices/jumpIndicatorsSlice.ts
/**
 * Phase 2: Jump Indicator Layout Fix - Redux Slice
 * 
 * Enhanced jump indicators with:
 * - Line assignment (primary/secondary)
 * - Layout settings for chain behavior
 * - Persistence support
 * 
 * Based on DNL Enhancement Specification v1.0, Section 3.3
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { nanoid } from 'nanoid';
import type {
  Jump,
  JumpIndicatorsState,
  JumpLayoutSettings,
  AddJumpPayload,
  UpdateJumpEndpointsPayload,
  SetJumpLinePayload,
  UpdateLayoutSettingsPayload,
} from '../../types/jump';
import {
  DEFAULT_JUMP_LAYOUT_SETTINGS,
} from '../../types/jump';

// =============================================================================
// STORAGE
// =============================================================================

const STORAGE_KEY = 'nl:jumpIndicators';
const LAYOUT_SETTINGS_KEY = 'nl:jumpLayoutSettings';

/**
 * Load layout settings from localStorage
 */
function loadLayoutSettings(): JumpLayoutSettings {
  try {
    const stored = localStorage.getItem(LAYOUT_SETTINGS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with defaults to ensure new properties are included (backward compatibility)
      return { ...DEFAULT_JUMP_LAYOUT_SETTINGS, ...parsed };
    }
  } catch (error) {
    console.error('Error loading jump layout settings:', error);
  }
  return DEFAULT_JUMP_LAYOUT_SETTINGS;
}

/**
 * Save layout settings to localStorage
 */
function saveLayoutSettings(settings: JumpLayoutSettings): void {
  try {
    localStorage.setItem(LAYOUT_SETTINGS_KEY, JSON.stringify(settings));
  } catch (error) {
    console.error('Error saving jump layout settings:', error);
  }
}

// =============================================================================
// INITIAL STATE
// =============================================================================

const initialState: JumpIndicatorsState = {
  jumps: [],
  nextId: 1,
  layoutSettings: loadLayoutSettings(),
};

// =============================================================================
// SLICE
// =============================================================================

export const jumpIndicatorsSlice = createSlice({
  name: 'jumpIndicators',
  initialState,
  reducers: {
    // =========================================================================
    // JUMP CRUD
    // =========================================================================

    /**
     * Add a new jump indicator
     * Phase 2: Now supports line assignment
     */
    addJump: (state, action: PayloadAction<AddJumpPayload>) => {
      const { startValue, endValue, line = 'primary', label } = action.payload;
      const direction = endValue >= startValue ? 'positive' : 'negative';
      const color = direction === 'positive' ? '#22c55e' : '#ef4444';
      
      const jump: Jump = {
        id: nanoid(),
        startValue,
        endValue,
        autoLabel: !label,
        label,
        color,
        direction,
        selected: false,
        line, // NEW: Line assignment
        labelVisible: true, // Default to visible
      };
      
      state.jumps.push(jump);
      state.nextId += 1;
    },

    /**
     * Update jump endpoints
     */
    updateJumpEndpoints: (state, action: PayloadAction<UpdateJumpEndpointsPayload>) => {
      const { id, startValue, endValue } = action.payload;
      const jump = state.jumps.find(j => j.id === id);
      if (jump) {
        jump.startValue = startValue;
        jump.endValue = endValue;
        
        // Update direction and color
        const direction = endValue >= startValue ? 'positive' : 'negative';
        jump.direction = direction;
        jump.color = direction === 'positive' ? '#22c55e' : '#ef4444';
      }
    },

    /**
     * Set jump label (disables auto-labeling)
     */
    setJumpLabel: (state, action: PayloadAction<{ id: string; label: string }>) => {
      const { id, label } = action.payload;
      const jump = state.jumps.find(j => j.id === id);
      if (jump) {
        jump.label = label;
        jump.autoLabel = false;
      }
    },

    /**
     * Toggle auto-labeling for a jump
     */
    toggleJumpAutoLabel: (state, action: PayloadAction<string>) => {
      const jump = state.jumps.find(j => j.id === action.payload);
      if (jump) {
        jump.autoLabel = !jump.autoLabel;
        if (jump.autoLabel) {
          jump.label = undefined;
        }
      }
    },

    /**
     * Select/deselect a jump
     */
    setJumpSelected: (state, action: PayloadAction<{ id: string; selected: boolean }>) => {
      const { id, selected } = action.payload;
      const jump = state.jumps.find(j => j.id === id);
      if (jump) {
        jump.selected = selected;
      }
    },

    /**
     * Toggle jump selection (with optional ctrlKey for multi-select)
     */
    toggleJumpSelection: (state, action: PayloadAction<{ id: string; ctrlKey?: boolean }>) => {
      const { id, ctrlKey } = action.payload;
      const jump = state.jumps.find(j => j.id === id);
      if (!jump) return;

      if (ctrlKey) {
        // Multi-select: toggle this jump's selection state
        jump.selected = !jump.selected;
      } else {
        // Single-select: deselect all others, then toggle this one
        state.jumps.forEach(j => {
          if (j.id !== id) {
            j.selected = false;
          }
        });
        jump.selected = !jump.selected;
      }
    },

    /**
     * Delete a jump
     */
    deleteJump: (state, action: PayloadAction<string>) => {
      state.jumps = state.jumps.filter(j => j.id !== action.payload);
    },

    /**
     * Delete all selected jumps
     */
    deleteSelectedJumps: (state) => {
      state.jumps = state.jumps.filter(j => !j.selected);
    },

    /**
     * Duplicate a jump
     */
    duplicateJump: (state, action: PayloadAction<string>) => {
      const original = state.jumps.find(j => j.id === action.payload);
      if (original) {
        const duplicate: Jump = {
          ...original,
          id: nanoid(),
          selected: false,
          startValue: original.startValue + 1,
          endValue: original.endValue + 1,
        };
        state.jumps.push(duplicate);
      }
    },

    /**
     * Clear all jumps
     */
    clearJumps: (state) => {
      state.jumps = [];
    },

    /**
     * Deselect all jumps
     */
    deselectAllJumps: (state) => {
      state.jumps.forEach(jump => {
        jump.selected = false;
      });
    },

    // =========================================================================
    // PHASE 2: LINE ASSIGNMENT
    // =========================================================================

    /**
     * Set which line a jump belongs to
     * NEW in Phase 2
     */
    setJumpLine: (state, action: PayloadAction<SetJumpLinePayload>) => {
      const { id, line } = action.payload;
      const jump = state.jumps.find(j => j.id === id);
      if (jump) {
        jump.line = line;
      }
    },

    /**
     * Move all selected jumps to a specific line
     * NEW in Phase 2
     */
    moveSelectedJumpsToLine: (state, action: PayloadAction<'primary' | 'secondary'>) => {
      const targetLine = action.payload;
      state.jumps.forEach(jump => {
        if (jump.selected) {
          jump.line = targetLine;
        }
      });
    },

    /**
     * Toggle jump line between primary and secondary
     * NEW in Phase 2
     */
    toggleJumpLine: (state, action: PayloadAction<string>) => {
      const jump = state.jumps.find(j => j.id === action.payload);
      if (jump) {
        jump.line = jump.line === 'primary' ? 'secondary' : 'primary';
      }
    },

    // =========================================================================
    // PHASE 2: LAYOUT SETTINGS
    // =========================================================================

    /**
     * Toggle chain sequential jumps setting
     * When true, sequential jumps are rendered side-by-side
     * NEW in Phase 2
     */
    toggleChainSequentialJumps: (state) => {
      state.layoutSettings.chainSequentialJumps = !state.layoutSettings.chainSequentialJumps;
      saveLayoutSettings(state.layoutSettings);
    },

    /**
     * Set chain sequential jumps setting explicitly
     * NEW in Phase 2
     */
    setChainSequentialJumps: (state, action: PayloadAction<boolean>) => {
      state.layoutSettings.chainSequentialJumps = action.payload;
      saveLayoutSettings(state.layoutSettings);
    },

    /**
     * Update layout settings
     * NEW in Phase 2
     */
    updateLayoutSettings: (state, action: PayloadAction<UpdateLayoutSettingsPayload>) => {
      state.layoutSettings = {
        ...state.layoutSettings,
        ...action.payload,
      };
      saveLayoutSettings(state.layoutSettings);
    },

    /**
     * Reset layout settings to defaults
     * NEW in Phase 2
     */
    resetLayoutSettings: (state) => {
      state.layoutSettings = DEFAULT_JUMP_LAYOUT_SETTINGS;
      saveLayoutSettings(state.layoutSettings);
    },

    // =========================================================================
    // BULK OPERATIONS
    // =========================================================================

    /**
     * Set color for selected jumps
     */
    setSelectedJumpsColor: (state, action: PayloadAction<string>) => {
      const color = action.payload;
      state.jumps.forEach(jump => {
        if (jump.selected) {
          jump.color = color;
        }
      });
    },

    /**
     * Import jumps (for scene restoration)
     */
    importJumps: (state, action: PayloadAction<Jump[]>) => {
      state.jumps = action.payload;
      state.nextId = Math.max(...action.payload.map(j => parseInt(j.id) || 0), state.nextId) + 1;
    },

    /**
     * Load jump indicators state from shared scene
     */
    loadJumpIndicatorsState: (state, action: PayloadAction<JumpIndicatorsState>) => {
      Object.assign(state, action.payload);
    },

    /**
     * Toggle label visibility for a jump
     */
    toggleJumpLabelVisibility: (state, action: PayloadAction<string>) => {
      const jump = state.jumps.find(j => j.id === action.payload);
      if (jump) {
        // Default to true if not set, so toggle from undefined/true -> false, false -> true
        jump.labelVisible = jump.labelVisible === false ? true : false;
      }
    },

    /**
     * Reverse a jump (swap start and end values)
     */
    reverseJump: (state, action: PayloadAction<string>) => {
      const jump = state.jumps.find(j => j.id === action.payload);
      if (jump) {
        const temp = jump.startValue;
        jump.startValue = jump.endValue;
        jump.endValue = temp;
        
        // Update direction and color
        const direction = jump.endValue >= jump.startValue ? 'positive' : 'negative';
        jump.direction = direction;
        jump.color = direction === 'positive' ? '#22c55e' : '#ef4444';
      }
    },
  },
});

// =============================================================================
// EXPORTS
// =============================================================================

export const {
  // CRUD
  addJump,
  updateJumpEndpoints,
  setJumpLabel,
  toggleJumpAutoLabel,
  setJumpSelected,
  toggleJumpSelection,
  deleteJump,
  deleteSelectedJumps,
  duplicateJump,
  clearJumps,
  deselectAllJumps,
  // Phase 2: Line assignment
  setJumpLine,
  moveSelectedJumpsToLine,
  toggleJumpLine,
  // Phase 2: Layout settings
  toggleChainSequentialJumps,
  setChainSequentialJumps,
  updateLayoutSettings,
  resetLayoutSettings,
  // Bulk operations
  setSelectedJumpsColor,
  importJumps,
  // Scene loading
  loadJumpIndicatorsState,
  // Label visibility
  toggleJumpLabelVisibility,
  // Reverse jump
  reverseJump,
} = jumpIndicatorsSlice.actions;

export default jumpIndicatorsSlice.reducer;

// =============================================================================
// SELECTORS
// =============================================================================

import type { RootState } from '../../types/redux';

/**
 * Select all jumps
 */
export const selectAllJumps = (state: RootState) => state.jumpIndicators.jumps;

/**
 * Select jumps for a specific line
 * NEW in Phase 2
 */
export const selectJumpsByLine = (line: 'primary' | 'secondary') => (state: RootState): Jump[] =>
  state.jumpIndicators.jumps.filter((j: Jump) => j.line === line);

/**
 * Select primary line jumps
 * NEW in Phase 2
 */
export const selectPrimaryJumps = (state: RootState): Jump[] =>
  state.jumpIndicators.jumps.filter((j: Jump) => j.line === 'primary');

/**
 * Select secondary line jumps
 * NEW in Phase 2
 */
export const selectSecondaryJumps = (state: RootState): Jump[] =>
  state.jumpIndicators.jumps.filter((j: Jump) => j.line === 'secondary');

/**
 * Select selected jumps
 */
export const selectSelectedJumps = (state: RootState): Jump[] =>
  state.jumpIndicators.jumps.filter((j: Jump) => j.selected);

/**
 * Select layout settings
 * NEW in Phase 2
 */
export const selectLayoutSettings = (state: RootState): JumpLayoutSettings =>
  state.jumpIndicators.layoutSettings;

/**
 * Select chain sequential jumps setting
 * NEW in Phase 2
 */
export const selectChainSequentialJumps = (state: RootState): boolean =>
  state.jumpIndicators.layoutSettings.chainSequentialJumps;
